import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DatePickerWithRange } from '@/components/ui/date-picker-with-range';
import { 
  BarChart3, 
  TrendingUp, 
  Clock, 
  Star, 
  Download, 
  Filter,
  Mail,
  AlertTriangle,
  CheckCircle,
  Users
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { format, subDays } from 'date-fns';
import { DateRange } from 'react-day-picker';
import { toast } from 'sonner';

interface ContactAnalytics {
  date: string;
  total_submissions: number;
  submissions_by_category: any;
  submissions_by_priority: any;
  average_response_time_hours: number;
  resolution_rate: number;
}

interface AnalyticsStats {
  totalSubmissions: number;
  avgResponseTime: number;
  resolutionRate: number;
  priorityBreakdown: Record<string, number>;
  categoryBreakdown: Record<string, number>;
  trendData: ContactAnalytics[];
}

const ContactAnalyticsDashboard = () => {
  const [stats, setStats] = useState<AnalyticsStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: subDays(new Date(), 30),
    to: new Date()
  });
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [categories, setCategories] = useState<Array<{ id: string; name: string }>>([]);

  useEffect(() => {
    fetchCategories();
    fetchAnalytics();
  }, [dateRange, selectedCategory]);

  const fetchCategories = async () => {
    try {
      const { data } = await supabase
        .from('contact_categories')
        .select('id, name')
        .eq('is_active', true)
        .order('name');
      
      setCategories(data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const fromDate = dateRange?.from ? format(dateRange.from, 'yyyy-MM-dd') : format(subDays(new Date(), 30), 'yyyy-MM-dd');
      const toDate = dateRange?.to ? format(dateRange.to, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd');

      // Fetch analytics data
      const { data: analyticsData } = await supabase
        .from('contact_analytics')
        .select('*')
        .gte('date', fromDate)
        .lte('date', toDate)
        .order('date');

      // Fetch real-time submission data for comparison
      let submissionsQuery = supabase
        .from('contact_submissions')
        .select(`
          *,
          contact_categories(name)
        `)
        .gte('created_at', fromDate + 'T00:00:00Z')
        .lte('created_at', toDate + 'T23:59:59Z');

      if (selectedCategory !== 'all') {
        submissionsQuery = submissionsQuery.eq('category_id', selectedCategory);
      }

      const { data: submissions } = await submissionsQuery;

      // Calculate aggregated stats
      const totalSubmissions = submissions?.length || 0;
      const resolvedSubmissions = submissions?.filter(s => s.status === 'resolved').length || 0;
      const resolutionRate = totalSubmissions > 0 ? (resolvedSubmissions / totalSubmissions) * 100 : 0;
      
      const responseTimes = submissions?.filter(s => s.response_time_hours).map(s => s.response_time_hours) || [];
      const avgResponseTime = responseTimes.length > 0 
        ? responseTimes.reduce((sum, time) => sum + time, 0) / responseTimes.length 
        : 0;

      // Group by priority
      const priorityBreakdown = submissions?.reduce((acc, submission) => {
        acc[submission.priority] = (acc[submission.priority] || 0) + 1;
        return acc;
      }, {} as Record<string, number>) || {};

      // Group by category
      const categoryBreakdown = submissions?.reduce((acc, submission) => {
        const categoryName = submission.contact_categories?.name || 'Uncategorized';
        acc[categoryName] = (acc[categoryName] || 0) + 1;
        return acc;
      }, {} as Record<string, number>) || {};

      setStats({
        totalSubmissions,
        avgResponseTime,
        resolutionRate,
        priorityBreakdown,
        categoryBreakdown,
        trendData: analyticsData || []
      });
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const exportData = async () => {
    try {
      const fromDate = dateRange?.from ? format(dateRange.from, 'yyyy-MM-dd') : format(subDays(new Date(), 30), 'yyyy-MM-dd');
      const toDate = dateRange?.to ? format(dateRange.to, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd');

      let query = supabase
        .from('contact_submissions')
        .select(`
          name,
          email,
          phone,
          subject,
          message,
          status,
          priority,
          created_at,
          first_response_at,
          response_time_hours,
          contact_categories(name),
          profiles(name)
        `)
        .gte('created_at', fromDate + 'T00:00:00Z')
        .lte('created_at', toDate + 'T23:59:59Z')
        .order('created_at', { ascending: false });

      if (selectedCategory !== 'all') {
        query = query.eq('category_id', selectedCategory);
      }

      const { data: exportData } = await query;

      if (!exportData) {
        toast.error('No data to export');
        return;
      }

      // Convert to CSV
      const headers = [
        'Name', 'Email', 'Phone', 'Subject', 'Message', 'Status', 'Priority',
        'Category', 'Submitted At', 'First Response At', 'Response Time (Hours)', 'Assigned To'
      ];

      const csvContent = [
        headers.join(','),
        ...exportData.map(row => [
          `"${row.name || ''}"`,
          `"${row.email || ''}"`,
          `"${row.phone || ''}"`,
          `"${row.subject || ''}"`,
          `"${(row.message || '').replace(/"/g, '""')}"`,
          `"${row.status || ''}"`,
          `"${row.priority || ''}"`,
          `"${row.contact_categories?.name || ''}"`,
          `"${row.created_at ? format(new Date(row.created_at), 'yyyy-MM-dd HH:mm:ss') : ''}"`,
          `"${row.first_response_at ? format(new Date(row.first_response_at), 'yyyy-MM-dd HH:mm:ss') : ''}"`,
          `"${row.response_time_hours || ''}"`,
          `"${row.profiles?.name || ''}"`
        ].join(','))
      ].join('\n');

      // Download file
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `contact-submissions-${fromDate}-to-${toDate}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      toast.success('Data exported successfully');
    } catch (error) {
      console.error('Error exporting data:', error);
      toast.error('Failed to export data');
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority.toLowerCase()) {
      case 'urgent': return 'text-red-600 bg-red-50 border-red-200';
      case 'high': return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'medium': return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'low': return 'text-green-600 bg-green-50 border-green-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-foreground">Contact Analytics</h2>
          <div className="flex gap-2">
            <div className="h-10 w-48 bg-muted animate-pulse rounded-md" />
            <div className="h-10 w-32 bg-muted animate-pulse rounded-md" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-32 bg-muted animate-pulse rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Contact Analytics</h2>
          <p className="text-muted-foreground">Track contact volume, response times, and resolution rates</p>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="w-full sm:w-auto p-2 border rounded-md bg-background">
            <span className="text-sm text-muted-foreground">Date Range Filter</span>
          </div>
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-full sm:w-48">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map((category) => (
                <SelectItem key={category.id} value={category.id}>
                  {category.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={exportData} variant="outline" className="w-full sm:w-auto">
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-950/50 dark:to-blue-900/50 border-blue-200">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-blue-700 dark:text-blue-300">
              Total Submissions
            </CardTitle>
            <Mail className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-900 dark:text-blue-100">
              {stats?.totalSubmissions || 0}
            </div>
            <p className="text-xs text-blue-600 dark:text-blue-400">
              in selected period
            </p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-950/50 dark:to-orange-900/50 border-orange-200">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-orange-700 dark:text-orange-300">
              Avg Response Time
            </CardTitle>
            <Clock className="h-4 w-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-900 dark:text-orange-100">
              {stats?.avgResponseTime ? `${stats.avgResponseTime.toFixed(1)}h` : 'N/A'}
            </div>
            <p className="text-xs text-orange-600 dark:text-orange-400">
              first response time
            </p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-950/50 dark:to-green-900/50 border-green-200">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-green-700 dark:text-green-300">
              Resolution Rate
            </CardTitle>
            <CheckCircle className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-900 dark:text-green-100">
              {stats?.resolutionRate ? `${stats.resolutionRate.toFixed(1)}%` : 'N/A'}
            </div>
            <p className="text-xs text-green-600 dark:text-green-400">
              of total submissions
            </p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-950/50 dark:to-purple-900/50 border-purple-200">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-purple-700 dark:text-purple-300">
              High Priority
            </CardTitle>
            <AlertTriangle className="h-4 w-4 text-purple-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-900 dark:text-purple-100">
              {(stats?.priorityBreakdown?.urgent || 0) + (stats?.priorityBreakdown?.high || 0)}
            </div>
            <p className="text-xs text-purple-600 dark:text-purple-400">
              urgent & high priority
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Priority Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Priority Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(stats?.priorityBreakdown || {}).map(([priority, count]) => (
                <div key={priority} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className={`px-2 py-1 rounded-md border text-xs font-medium ${getPriorityColor(priority)}`}>
                      {priority.charAt(0).toUpperCase() + priority.slice(1)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">{count}</span>
                    <div className="w-20 h-2 bg-muted rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-primary transition-all duration-300"
                        style={{ 
                          width: `${stats?.totalSubmissions ? (count / stats.totalSubmissions) * 100 : 0}%` 
                        }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Category Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(stats?.categoryBreakdown || {}).map(([category, count]) => (
                <div key={category} className="flex items-center justify-between">
                  <span className="text-sm font-medium truncate">{category}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">{count}</span>
                    <div className="w-20 h-2 bg-muted rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-secondary transition-all duration-300"
                        style={{ 
                          width: `${stats?.totalSubmissions ? (count / stats.totalSubmissions) * 100 : 0}%` 
                        }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ContactAnalyticsDashboard;